import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trash2, Mail, Phone, MapPin, Building2, MessageSquare, Download, Search } from "lucide-react";
import { PageHeader, EmptyState, Badge } from "../components/Brand";
import { useApp, API } from "../AppContext";
import SubscriptionGate from "../components/SubscriptionGate";

const STATUSES = ["new", "contacted", "qualified", "closed_won", "closed_lost"];

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  return d.toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
};

export default function ErpLeads() {
  const { lang } = useApp();
  return (
    <SubscriptionGate featureName={lang === "fr" ? "Le module Demandes ERP" : "The ERP Leads module"}>
      <ErpLeadsInner />
    </SubscriptionGate>
  );
}

function ErpLeadsInner() {
  const { t, lang } = useApp();
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [query, setQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = async () => {
    const { data } = await axios.get(`${API}/admin/erp-leads`);
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const filteredLeads = leads.filter((l) => {
    if (filterStatus !== "all" && (l.status || "new") !== filterStatus) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [l.name, l.company, l.email, l.phone, l.city, l.work_type, l.message]
      .some((f) => (f || "").toLowerCase().includes(q));
  });

  const exportCsv = async () => {
    const token = localStorage.getItem("progere_token");
    const res = await fetch(`${API}/admin/erp-leads/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `progere-erp-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const open = async (lead) => {
    setSelected(lead);
    setNotes(lead.notes || "");
    if (!lead.read) {
      await axios.patch(`${API}/admin/erp-leads/${lead.id}`, { read: true });
      load();
    }
  };

  const setStatus = async (s) => {
    if (!selected) return;
    await axios.patch(`${API}/admin/erp-leads/${selected.id}`, { status: s });
    setSelected({ ...selected, status: s });
    load();
  };

  const saveNotes = async () => {
    if (!selected) return;
    await axios.patch(`${API}/admin/erp-leads/${selected.id}`, { notes });
    setSelected({ ...selected, notes });
    load();
  };

  const remove = async (lead) => {
    if (!confirm(lang === "fr" ? "Supprimer cette demande ?" : "Delete this request?")) return;
    await axios.delete(`${API}/admin/erp-leads/${lead.id}`);
    if (selected?.id === lead.id) setSelected(null);
    load();
  };

  const unreadCount = leads.filter((l) => !l.read).length;

  return (
    <div>
      <PageHeader
        title={t.erp_admin.title}
        subtitle={`${t.erp_admin.sub} · ${leads.length} total${unreadCount ? ` · ${unreadCount} ${lang === "fr" ? "non lues" : "unread"}` : ""}`}
        action={leads.length > 0 && (
          <button onClick={exportCsv} className="btn-ghost text-sm" data-testid="export-csv">
            <Download size={14} color="#D4AF37" /> {lang === "fr" ? "Exporter CSV" : "Export CSV"}
          </button>
        )}
      />

      {leads.length === 0 ? (
        <EmptyState title={t.erp_admin.empty} icon={<Building2 size={24} color="#D4AF37" />} />
      ) : (
        <>
          <div className="glass-card p-4 mb-6 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2" color="rgba(245,245,245,0.4)" />
              <input className="input-dark pl-10" placeholder={lang === "fr" ? "Rechercher nom, entreprise, courriel..." : "Search name, company, email..."}
                value={query} onChange={(e) => setQuery(e.target.value)} data-testid="erp-search" />
            </div>
            <div className="flex flex-wrap gap-2">
              {["all", ...STATUSES].map((s) => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  className="text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full transition"
                  style={filterStatus === s
                    ? { background: "rgba(212,175,55,0.18)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.5)" }
                    : { background: "rgba(255,255,255,0.04)", color: "rgba(245,245,245,0.55)", border: "1px solid rgba(255,255,255,0.08)" }}
                  data-testid={`filter-${s}`}>
                  {s === "all" ? (lang === "fr" ? "Toutes" : "All") : (t.erp_admin[s] || s)}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Leads list */}
          <div className="lg:col-span-1 space-y-3 max-h-[calc(100vh-22rem)] overflow-y-auto pr-1">
            {filteredLeads.length === 0 ? (
              <p className="text-sm py-8 text-center" style={{ color: "rgba(245,245,245,0.5)" }}>
                {lang === "fr" ? "Aucun résultat" : "No results"}
              </p>
            ) : filteredLeads.map((l) => (
              <button key={l.id} onClick={() => open(l)}
                className={`w-full text-left glass-card p-4 transition hover:border-[#D4AF37]/40 ${selected?.id === l.id ? "ring-1 ring-[#D4AF37]" : ""}`}
                style={!l.read ? { borderLeft: "3px solid #C00000" } : {}}
                data-testid={`lead-${l.id}`}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-semibold text-sm" style={{ color: "#F5F5F5" }}>{l.name}</div>
                  {!l.read && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0 mt-1.5" />}
                </div>
                <div className="text-xs mb-2" style={{ color: "#D4AF37" }}>{l.company}</div>
                <div className="text-[11px]" style={{ color: "rgba(245,245,245,0.5)" }}>{fmtDate(l.created_at)}</div>
                {l.status && l.status !== "new" && (
                  <span className="badge badge-approved mt-2 inline-flex text-[10px]">{t.erp_admin[l.status] || l.status}</span>
                )}
              </button>
            ))}
          </div>

          {/* Lead detail */}
          <div className="lg:col-span-2">
            {selected ? (
              <div className="glass-card p-6">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                  <div>
                    <h3 className="font-heading text-2xl font-bold" style={{ color: "#F5F5F5" }}>{selected.name}</h3>
                    <p className="text-sm" style={{ color: "#D4AF37" }}>{selected.company}</p>
                  </div>
                  <button onClick={() => remove(selected)} className="btn-ghost text-xs py-2 px-3" data-testid="lead-delete">
                    <Trash2 size={14} color="#C00000" /> {t.erp_admin.delete}
                  </button>
                </div>

                {/* Status pills */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(s)}
                      className={`text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full transition ${selected.status === s ? "" : "hover:bg-white/5"}`}
                      style={selected.status === s
                        ? { background: "rgba(212,175,55,0.18)", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.5)" }
                        : { background: "rgba(255,255,255,0.04)", color: "rgba(245,245,245,0.55)", border: "1px solid rgba(255,255,255,0.08)" }}
                      data-testid={`lead-status-${s}`}>
                      {t.erp_admin[s] || s}
                    </button>
                  ))}
                </div>

                {/* Contact info grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-3 p-3 rounded-2xl hover:bg-white/5 transition"
                     style={{ background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.15)" }}>
                    <Mail size={16} color="#D4AF37" />
                    <span className="text-sm break-all" style={{ color: "#F5F5F5" }}>{selected.email}</span>
                  </a>
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} className="flex items-center gap-3 p-3 rounded-2xl hover:bg-white/5 transition"
                       style={{ background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.15)" }}>
                      <Phone size={16} color="#D4AF37" />
                      <span className="text-sm" style={{ color: "#F5F5F5" }}>{selected.phone}</span>
                    </a>
                  )}
                  {selected.city && (
                    <div className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.15)" }}>
                      <MapPin size={16} color="#D4AF37" />
                      <span className="text-sm" style={{ color: "#F5F5F5" }}>{selected.city}</span>
                    </div>
                  )}
                  {selected.team_size && (
                    <div className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.15)" }}>
                      <Building2 size={16} color="#D4AF37" />
                      <span className="text-sm" style={{ color: "#F5F5F5" }}>{selected.team_size}</span>
                    </div>
                  )}
                </div>

                {(selected.rbq_number || selected.work_type) && (
                  <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
                    {selected.rbq_number && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.5)" }}>RBQ</div>
                        <div style={{ color: "#F5F5F5" }}>{selected.rbq_number}</div>
                      </div>
                    )}
                    {selected.work_type && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Type de travaux" : "Work type"}</div>
                        <div style={{ color: "#F5F5F5" }}>{selected.work_type}</div>
                      </div>
                    )}
                  </div>
                )}

                {selected.message && (
                  <div className="mb-6">
                    <div className="text-[10px] uppercase tracking-wider mb-2 flex items-center gap-2" style={{ color: "#D4AF37" }}>
                      <MessageSquare size={12} /> Message
                    </div>
                    <p className="text-sm whitespace-pre-line p-4 rounded-2xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", color: "rgba(245,245,245,0.85)", lineHeight: 1.6 }}>
                      {selected.message}
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: "rgba(245,245,245,0.5)" }}>{lang === "fr" ? "Notes internes" : "Internal notes"}</div>
                  <textarea className="input-dark" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t.erp_admin.notes_placeholder} data-testid="lead-notes" />
                  <button onClick={saveNotes} className="btn-ghost text-xs mt-2" data-testid="lead-save-notes">{t.erp_admin.save_notes}</button>
                </div>

                <p className="text-xs mt-6" style={{ color: "rgba(245,245,245,0.4)" }}>
                  {lang === "fr" ? "Reçue le" : "Received"} {fmtDate(selected.created_at)}
                </p>
              </div>
            ) : (
              <div className="glass-card p-12 text-center">
                <Building2 size={36} color="rgba(212,175,55,0.5)" className="mx-auto mb-4" />
                <p className="text-sm" style={{ color: "rgba(245,245,245,0.55)" }}>
                  {lang === "fr" ? "Sélectionnez une demande pour voir les détails" : "Select a request to view details"}
                </p>
              </div>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
}
